import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { useApiClient } from '../contexts/apiClient';

export interface IEventLandingItem {
	id: string;
	name: string;
	startTime: string;
	endTime?: string;
	timeZone?: string;
	location?: string;
	details?: string;
	imageUrl?: string;
	anonymousLink: string;
	isPaidEvent?: boolean;
	registrationClosed: boolean;
	isSoldOut: boolean;
}

export interface IEventsLandingPage {
	accountName: string;
	events: IEventLandingItem[];
	pastEvents: IEventLandingItem[];
	totalCount: number;
}

/** Matches flattened backend PublicEventInfo DTO — see Levitate-API PR #10616 */
interface IBackendPublicEventInfo {
	id: string;
	name: string;
	location?: string;
	startTime: string;
	endTime?: string;
	timeZone?: string;
	details?: string;
	imageUrl?: string;
	isPaidEvent: boolean;
	eventsLandingPageUrl: string;
	anonymousLink: string;
	registrationClosed: boolean;
	isSoldOut: boolean;
}

interface IBackendEventsLandingResponse {
	events: IBackendPublicEventInfo[];
	totalCount: number;
	account: {
		companyName: string;
	};
}

const PAGE_SIZE = 50;

const mapEvent = (event: IBackendPublicEventInfo): IEventLandingItem => ({
	id: event.id,
	name: event.name,
	startTime: event.startTime,
	endTime: event.endTime,
	timeZone: event.timeZone,
	location: event.location,
	details: event.details,
	imageUrl: event.imageUrl,
	anonymousLink: event.anonymousLink,
	isPaidEvent: event.isPaidEvent,
	registrationClosed: event.registrationClosed,
	isSoldOut: event.isSoldOut,
});

function mapBackendPage(response: IBackendEventsLandingResponse, now: Date): IEventsLandingPage {
	// Filter out events missing required fields or with invalid dates
	const isValidDate = (dateStr: string) => !isNaN(new Date(dateStr).getTime());
	const validEvents = response.events.filter(
		event =>
			event.id &&
			event.name &&
			event.anonymousLink &&
			event.startTime &&
			isValidDate(event.startTime) &&
			(!event.endTime || isValidDate(event.endTime))
	);

	const current = validEvents
		.filter(event => {
			const eventDate = event.endTime || event.startTime;
			return new Date(eventDate) >= now;
		})
		.map(mapEvent);

	const past = validEvents
		.filter(event => {
			const eventDate = event.endTime || event.startTime;
			return new Date(eventDate) < now;
		})
		.map(mapEvent);

	return {
		accountName: response.account?.companyName || '',
		events: current,
		pastEvents: past,
		totalCount: response.totalCount,
	};
}

export interface IEventsLandingOptions {
	shortCode: string;
	startDate?: Date;
	endDate?: Date;
}

export const useEventsLanding = (options: IEventsLandingOptions) => {
	const { baseUrl } = useApiClient();
	const { shortCode, startDate, endDate } = options;

	return useInfiniteQuery<IEventsLandingPage, Error>({
		queryKey: ['EventsLanding', shortCode, startDate?.toISOString(), endDate?.toISOString()],
		queryFn: async ({ pageParam }) => {
			const now = new Date();
			const params = new URLSearchParams();
			params.append('includeExpired', 'true');
			params.append('pageSize', String(PAGE_SIZE));
			params.append('pageNumber', String(pageParam));

			if (startDate) {
				params.append('startDate', startDate.toISOString().split('T')[0]);
			}
			if (endDate) {
				params.append('endDate', endDate.toISOString().split('T')[0]);
			}

			const url = `${baseUrl}/public/events/${encodeURIComponent(shortCode)}/landing?${params.toString()}`;
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`Failed to fetch events: ${response.statusText}`);
			}
			const result = await response.json();
			const data: IBackendEventsLandingResponse = result.value;
			if (!data || !Array.isArray(data.events)) {
				throw new Error('Unexpected response shape from events landing API');
			}
			return mapBackendPage(data, now);
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => {
			const fetchedCount = allPages.length * PAGE_SIZE;
			return fetchedCount < lastPage.totalCount ? allPages.length : undefined;
		},
		enabled: !!shortCode,
		staleTime: 60_000,
		retry: 1,
		placeholderData: keepPreviousData,
	});
};
